<template>
  <div class="mall-card-manage">
    <div class="page">
      <MallHeader @back-click="goBack">
        <div slot="right"></div>
      </MallHeader>

      <div v-if="isLoaded" class="mall-card-manage__content">
        <span class="mall-card-manage__heading">결제 카드 관리</span>

        <!-- 등록된 카드 -->
        <div v-if="card" class="mall-card-manage__card">
          <div class="mall-card-manage__card-icon">
            <img src="@/assets/mall/icn_card_color_small.svg" alt="" />
          </div>
          <div class="mall-card-manage__card-body">
            <div class="mall-card-manage__card-text">
              <span class="mall-card-manage__card-name">{{ card.card_name }}</span>
              <span class="mall-card-manage__card-number">{{ cardNumber }}</span>
            </div>
            <div class="mall-card-manage__card-buttons">
              <MallButton
                class="mall-card-manage__card-button"
                :label="'수정'"
                :type="'secondary'"
                :size="'sm'"
                @click="goRegister"
              />
              <MallButton
                class="mall-card-manage__card-button"
                :label="'삭제'"
                :type="'error'"
                :size="'sm'"
                :disabled="isRequesting"
                @click="isDeleteSheet = true"
              />
            </div>
          </div>
        </div>

        <!-- 등록된 카드가 없을 때 -->
        <div v-else class="mall-card-manage__empty">
          <div class="mall-card-manage__empty-text">
            <img src="@/assets/mall/icn_file_text_color.svg" alt="" />
            <span>카드 정보가 없어요</span>
          </div>
          <MallButton
            class="mall-card-manage__empty-button"
            :label="'카드 등록하기'"
            :type="'secondary'"
            :size="'md'"
            @click="goRegister"
          />
        </div>
      </div>

      <!-- 삭제 확인 (Figma에 없는 화면 - 카드 등록 취소 시트와 같은 형태) -->
      <MallNoticeSheet
        v-if="isDeleteSheet"
        :title="'카드를 삭제할까요?'"
        :messageLines="['삭제하면 결제할 때 카드를 다시 등록해야 해요.']"
        :cancelLabel="'취소'"
        :confirmLabel="'삭제하기'"
        :dim="'normal'"
        @cancel="isDeleteSheet = false"
        @close="isDeleteSheet = false"
        @confirm="deleteCard"
      />

      <MallToast
        v-if="toast"
        :key="toast.key"
        :title="toast.title"
        :message="toast.message"
        @close="toast = null"
      />
    </div>
  </div>
</template>

<script>
import Constant from '@/constant'
import mall from '@/utilities/mall.js'
import MallButton from '@/components/new/atoms/MallButton.vue'
import MallHeader from '@/components/new/molecules/MallHeader.vue'
import MallToast from '@/components/new/molecules/MallToast.vue'
import MallNoticeSheet from '@/components/new/organisms/MallNoticeSheet.vue'

// 결제 카드 관리 (프로필 하나에 카드 하나)
export default {
  name: 'MallCardManage',
  components: {
    MallButton,
    MallHeader,
    MallToast,
    MallNoticeSheet,
  },
  data() {
    return {
      profileId: Number(this.$route.query.profileId) || null,
      token: null,
      // 프로필의 card_info { card_name, card_number(마스킹), card_nick_name, company_code }, 없으면 null
      card: null,
      isLoaded: false,
      isRequesting: false,
      isDeleteSheet: false,
      toast: null,
    }
  },
  computed: {
    cardNumber() {
      return this.card ? mall.formatCardNumber(this.card.card_number) : ''
    },
  },
  mounted() {
    ChannelIO('shutdown')
    this.token = mall.getAccessToken()
    if (!this.token) {
      mall.goLogin(this.$router)
      return
    }
    mall.fetchProfile(this.$store, this.profileId, this.token)
      .then((profile) => {
        this.card = (profile && profile.card_info) || null
        this.isLoaded = true
      })
      .catch((err) => this.handleError(err, '카드 정보를 불러오지 못했어요.'))
  },
  methods: {
    // 구매하기에서 들어왔으면 구매하기로, 아니면 몰 홈으로
    goBack() {
      mall.goBack(this.$router, this.$route, { path: '/mall/shop', query: { profileId: this.profileId } })
    },
    // 수정도 등록과 같은 화면 (등록 API가 기존 카드를 교체함)
    // 등록 화면에서 돌아올 때 이 화면의 returnTo가 유지되도록 현재 주소를 전달
    goRegister() {
      this.$router.push({ path: '/mall/card/register', query: { profileId: this.profileId, returnTo: this.$route.fullPath } })
    },
    deleteCard() {
      if (this.isRequesting) return
      this.isDeleteSheet = false
      this.isRequesting = true
      this.$store.dispatch(Constant.DELETE_PROFILE_CARD, { profile_id: this.profileId, token: this.token })
        .then(() => {
          this.card = null
        })
        .catch((err) => this.handleError(err, '카드를 삭제하지 못했어요.'))
        .then(() => {
          this.isRequesting = false
        })
    },
    handleError(err, title) {
      if (mall.isAuthError(err)) {
        mall.goLogin(this.$router)
        return
      }
      this.toast = { key: Date.now(), title: title, message: mall.errorMessage(err) }
    },
  },
}
</script>

<style scoped>
  .mall-card-manage {
    display: flex;
    width: 100%;
    min-height: 100vh;
    justify-content: center;
    align-items: flex-start;
  }
  .page {
    width: 100%;
    min-height: 100vh;
    background: #FFF;
  }
  .mall-card-manage__content {
    display: flex;
    padding: 4px 16px 32px 16px;
    flex-direction: column;
    gap: 48px;
  }
  .mall-card-manage__heading {
    padding-left: 4px;
    color: var(--color-text-main);
    font-family: Pretendard;
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    line-height: var(--line-height-tight);
  }
  /* Figma 여백 17px / 13px(테두리 1px 포함) + 목록 항목 여백 2px */
  .mall-card-manage__card {
    display: flex;
    padding: 18px 14px;
    align-items: flex-start;
    gap: 12px;
    border: 1px solid #E5E8EB;
    border-radius: 12px;
    background: #F9FAFB;
  }
  /* 카드 아이콘은 24px 영역보다 넓게 배치된 그래픽 (Figma 기준 위치) */
  .mall-card-manage__card-icon {
    position: relative;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    overflow: hidden;
  }
  .mall-card-manage__card-icon img {
    position: absolute;
    top: 1.92px;
    left: -0.86px;
    display: block;
  }
  .mall-card-manage__card-body {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .mall-card-manage__card-text {
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 4px;
    font-family: Pretendard;
    font-weight: var(--font-weight-medium);
  }
  .mall-card-manage__card-name {
    color: var(--color-text-main);
    font-size: var(--font-size-md);
    line-height: var(--line-height-normal);
  }
  .mall-card-manage__card-number {
    color: #4E5968;
    font-size: var(--font-size-sm);
    line-height: var(--line-height-normal);
  }
  .mall-card-manage__card-buttons {
    display: flex;
    gap: 4px;
  }
  .mall-card-manage__card-button {
    width: 60px;
  }
  /* Figma 안쪽 테두리 기준 여백 32px / 16px (테두리 1px 포함) */
  .mall-card-manage__empty {
    display: flex;
    padding: 31px 15px;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    border: 1px solid #F2F4F6;
    border-radius: 12px;
    background: #FFF;
  }
  .mall-card-manage__empty-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #6B7684;
    font-family: Pretendard;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-normal);
  }
  .mall-card-manage__empty-text img {
    display: block;
  }
  /* MallButton 기본 flex: 1 이 세로 방향에서 높이를 줄이지 않도록 (선택자를 한 단계 더 구체적으로) */
  .mall-card-manage__empty .mall-card-manage__empty-button {
    flex: none;
    width: 240px;
  }
  @media (min-width: 600px) {
    .page {
      width: 600px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/page/new/MallCardManage.vue